import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PageHero from '../PageHero/PageHero';
import Footer from '../Footer/Footer';
import SendIcon from '@mui/icons-material/Send';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';


const ContactSection = styled(Box)(({ theme }) => ({
  padding: '80px 0',
  [theme.breakpoints.down('md')]: {
    padding: '60px 0'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '40px 0'
  }
}));

const ContactForm = styled(Paper)(({ theme }) => ({
  padding: '40px',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  [theme.breakpoints.down('sm')]: {
    padding: '24px'
  }
}));

const ContactInfo = styled(Paper)(({ theme }) => ({
  padding: '40px',
  borderRadius: '16px',
  backgroundColor: '#455b40',
  color: '#FFFFFF',
  height: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: '24px'
  }
}));

const InfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: '32px',
  '& svg': {
    color: '#85b47c',
    marginRight: '16px',
    fontSize: '24px',
    marginTop: '4px'
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '&:hover fieldset': {
      borderColor: '#85b47c'
    },
    '&.Mui-focused fieldset': {
      borderColor: '#455b40'
    }
  },
  '& .MuiInputLabel-root': {
    '&.Mui-focused': {
      color: '#455b40'
    }
  }
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#85b47c',
  color: '#FFFFFF',
  padding: '12px 32px',
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#6a8f61',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(133, 180, 124, 0.3)'
  },
  '&:disabled': {
    backgroundColor: '#cccccc'
  }
}));

interface FormData {
  name: string;
  number: string;
  email: string;
  affair: string;
  message: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    number: '',
    email: '',
    affair: '',
    message: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    const formDataToSend = new FormData();
    formDataToSend.append("_wpcf7", "346");
    formDataToSend.append("_wpcf7_version", "5.9.6");
    formDataToSend.append("_wpcf7_locale", "es_ES");
    formDataToSend.append("_wpcf7_unit_tag", "contacto");
    formDataToSend.append("_wpcf7_container_post", "0");
    formDataToSend.append("your-name", formData.name);
    formDataToSend.append("your-number", formData.number);
    formDataToSend.append("your-email", formData.email);
    formDataToSend.append("your-affair", formData.affair);
    formDataToSend.append("your-message", formData.message);

    try {
      const res = await fetch(
        "https://thomass362.sg-host.com/wp-json/contact-form-7/v1/contact-forms/346/feedback",
        {
          method: "POST",
          body: formDataToSend
        }
      );

      const data = await res.json();
      
      if (data.status === 'mail_sent') {
        setSuccess(true);
        setFormData({
          name: '',
          number: '',
          email: '',
          affair: '',
          message: ''
        });
      } else {
        setError(data.message || 'Error al enviar el formulario. Por favor, intente nuevamente.');
      }
    } catch (err) {
      setError('Error de conexión. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero 
        title="Contacto"
        subtitle="Estamos aquí para ayudarte"
        description="Contáctanos hoy mismo y te responderemos lo antes posible."
        backgroundImage="/Finca_ecológica_Valencia.jpg"
        showBreadcrumbs={true}
        breadcrumbs={[
          { label: 'Inicio', link: '/' },
          { label: 'Contacto', link: '/contacto' }
        ]}
        overlay={true}
        overlayOpacity={0.5}
      />

      <ContactSection>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
            <Box sx={{ flex: 1 }}>
              <ContactForm>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontFamily: '"Lora", serif',
                    fontWeight: 600,
                    mb: 3,
                    color: '#2a3a28'
                  }}
                >
                  Envíanos un mensaje
                </Typography>

                {success && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    ¡Mensaje enviado exitosamente! Nos pondremos en contacto pronto.
                  </Alert>
                )}

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <Stack spacing={3}>
                    <StyledTextField
                      fullWidth
                      label="Nombre completo"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />

                    <StyledTextField
                      fullWidth
                      label="Número de teléfono"
                      name="number"
                      value={formData.number}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />

                    <StyledTextField
                      fullWidth
                      label="Correo electrónico"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />

                    <StyledTextField
                      fullWidth
                      label="Asunto"
                      name="affair"
                      value={formData.affair}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />

                    <StyledTextField
                      fullWidth
                      label="Mensaje"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      multiline
                      rows={4}
                      required
                      disabled={loading}
                    />

                    <SubmitButton
                      type="submit"
                      disabled={loading}
                      endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    >
                      {loading ? 'Enviando...' : 'Enviar mensaje'}
                    </SubmitButton>
                  </Stack>
                </form>
              </ContactForm>
            </Box>

            <Box sx={{ flex: { xs: 1, md: 0.6 } }}>
              <ContactInfo>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontFamily: '"Lora", serif',
                    fontWeight: 600,
                    mb: 4
                  }}
                >
                  Información de contacto
                </Typography>

                <InfoItem>
                  <LocationOnIcon />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Dirección
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      C/ Les Llomes s/n. Bétera. 46117
                      <br />
                      Valencia, España
                    </Typography>
                  </Box>
                </InfoItem>

                <InfoItem>
                  <PhoneIcon />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Teléfono y WhatsApp
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    + 34 684 238 458
                    </Typography>
                  </Box>
                </InfoItem>

                <InfoItem>
                  <EmailIcon />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      info@ekkofood.com
                    </Typography>
                  </Box>
                </InfoItem>

                <Box sx={{ mt: 4, width: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Encuéntranos
                  </Typography>
                  <Box 
                    sx={{ 
                      width: '100%', 
                      height: '250px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '2px solid rgba(133, 180, 124, 0.3)'
                    }}
                  >
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3078.9856778547!2d-0.45742899999999995!3d39.6369221!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd605d4f1e506a4d%3A0x769ac3c79e4b470!2sEkkofood!5e0!3m2!1sen!2ses!4v1703123456789"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </Box>
                </Box>
              </ContactInfo>
            </Box>
          </Stack>
        </Container>
      </ContactSection>

      <Footer />
    </>
  );
};

export default Contact;